import axios, { AxiosResponse } from "axios";
import { NewUserPayload } from "@/types";
import logger from "@/configs/logger";
import * as queryStringUtil from "query-string";
import { auth } from "../configs/firebase";

const dashboardEndpoint = process.env.VUE_APP_DASHBOARD_URL;
const isProduction = process.env.NODE_ENV === "production";
const devDashboardToken = process.env.VUE_APP_DASHBOARD_DEV_TOKEN;

async function getToken(): Promise<string> {
  if (!isProduction) {
    return devDashboardToken;
  }

  const user = auth().currentUser;
  if (user) {
    let token = await user.getIdToken(true);
    logger("log", "refreshing token");
    return token;
  }
  return "";
}

export function postCustomerServiceNote(payload: any): Promise<AxiosResponse> {
  return axios.post(`${dashboardEndpoint}/note/${payload.leadId}`, payload);
}

export async function getCustomerServiceNotes(
  leadId: string
): Promise<AxiosResponse> {
  return axios.get(`${dashboardEndpoint}/note/${leadId}`);
}

export function sendText(message: any): Promise<AxiosResponse> {
  return axios.post("/send_text", {
    to: message.to,
    body: message.body
  });
}

export function sendEmail(message: any): Promise<AxiosResponse> {
  return axios.post("/send_email", {
    recipient: message.recipient,
    subject: message.subject,
    message: message.body
  });
}

export function adminCreateUser(
  payload: NewUserPayload
): Promise<AxiosResponse> {
  return axios.post("/admin_create_user", payload);
}

export function lookupLead(
  phoneNumber: string,
  opts?: { firstName?: string; lastName?: string; createIfMissing?: boolean }
) {
  const data: any = { phone_number: phoneNumber };
  if (opts) {
    if (opts.firstName) data["first_name"] = opts.firstName;
    if (opts.lastName) data["last_name"] = opts.lastName;
    if (opts.createIfMissing)
      data["create_lead_if_missing"] = opts.createIfMissing;
  }
  return axios({
    url: "/lookup_lead",
    method: "POST",
    data
  });
}

export function removeAddedCallFromTask(
  taskAttributeKey: string
): Promise<AxiosResponse> {
  return axios({
    url: "/remove_added_calls_from_task",
    method: "POST",
    data: { task_attribute_key: taskAttributeKey }
  });
}

export function getVerificationRecording(
  eventKey: string
): Promise<AxiosResponse> {
  return axios({
    url: "/snip_recording",
    method: "POST",
    responseType: "blob",
    data: { event_key: eventKey }
  });
}

export function updateTwilioTokens(): Promise<AxiosResponse> {
  return axios.post("/generate_twilio_token");
}

export function dialBack(taskAttributeKey: string): Promise<AxiosResponse> {
  return axios.post("/dial_agent_back_by_task ", {
    task_attribute: taskAttributeKey
  });
}

export function callBack(
  leadKey: string,
  alternatePhoneNumber: boolean
): Promise<AxiosResponse> {
  return axios.post("/callback_task", {
    lead_key: leadKey,
    alternate_phone_number: alternatePhoneNumber
  });
}

export function addToCall(
  phoneNumber: string,
  taskAttributeKey: string
): Promise<AxiosResponse> {
  return axios.post("/add_phone_number_to_task", {
    phone_number: phoneNumber,
    task_attribute_key: taskAttributeKey
  });
}

export async function transferCall(
  taskAttributeKey: string,
  to: string
): Promise<AxiosResponse> {
  const resp = await axios({
    url: "/transfer_call",
    method: "post",
    data: {
      task_attribute_key: taskAttributeKey,
      to: to
    }
  });
  return resp;
}

export async function adminJoinCall(
  mode: string,
  taskAttributeKey: string
): Promise<AxiosResponse> {
  const resp = await axios({
    url: "/admin_join_call",
    method: "post",
    data: {
      mode: mode,
      task_attribute_key: taskAttributeKey
    }
  });
  return resp;
}

export async function createDID(
  leadSource: string,
  bufferTime: string,
  taskCreationSource: string,
  requireDispositionNote: Boolean
): Promise<AxiosResponse> {
  const resp = await axios({
    url: "/create_did",
    method: "post",
    data: {
      lead_source: leadSource,
      buffer_time_s: bufferTime,
      task_creation_source: taskCreationSource,
      require_disposition_note: requireDispositionNote
    }
  });
  return resp;
}

export async function getSnippedRecordingDownloadLink(
  snippedRecordingKey: string
): Promise<AxiosResponse> {
  const resp = await axios({
    url: "/snipped_recording_download_link",
    method: "post",
    data: {
      snipped_recording: snippedRecordingKey
    }
  });
  return resp;
}

export async function adminDeleteAgent(uid: string): Promise<AxiosResponse> {
  return axios.post("/admin_delete_user", { uid });
}

export async function adminDisableAgent(
  uid: string,
  disable: boolean
): Promise<AxiosResponse> {
  return axios.post("/admin_disable_user", { uid, disable });
}

export async function adminGetLeads(query: Object): Promise<AxiosResponse> {
  const queryParams = queryStringUtil.stringify(query);
  const token = await getToken();
  const resp = await axios({
    url: `${dashboardEndpoint}/lead?${queryParams}`,
    method: "get",
    headers: {
      Authorization: token
    }
  });
  return resp;
}

export async function adminGetLeadSources(): Promise<AxiosResponse> {
  const token = await getToken();
  const resp = await axios({
    url: `${dashboardEndpoint}/lead-source`,
    method: "get",
    headers: {
      Authorization: token
    }
  });
  return resp;
}

export async function adminUpdateLeadCost(
  id: number,
  cost_from_manual_input: number
): Promise<AxiosResponse> {
  const token = await getToken();
  const resp = await axios({
    url: `${dashboardEndpoint}/lead/${id}`,
    method: "patch",
    data: {
      cost_from_manual_input
    },
    headers: {
      Authorization: token
    }
  });
  return resp;
}

export async function adminUpdateLeadSourcesCost(
  id: number,
  lead_cost: number
): Promise<AxiosResponse> {
  const token = await getToken();
  const resp = await axios({
    url: `${dashboardEndpoint}/lead-source/${id}`,
    method: "patch",
    data: {
      lead_cost
    },
    headers: {
      Authorization: token
    }
  });
  return resp;
}

export function sendBugToMonday(title: string): Promise<AxiosResponse> {
  return axios.post("/monday_bug", {
    title: title,
    source: "Frontend"
  });
}

// post new phone_number to blacklist
export async function addPhoneNumberToBlacklist(
  phoneNumber: string,
  blockedInbound: boolean,
  email: string
): Promise<AxiosResponse> {
  const token = await getToken();
  const resp = await axios({
    url: `/blacklist`,
    method: "post",
    data: {
      phone_number: phoneNumber,
      blocked_inbound: blockedInbound,
      posted_by: email
    },
    headers: {
      Authorization: token
    }
  });
  return resp;
}

// post new phone_number to blacklist
export async function sendFileLinkForBlacklist(
  fileUrl: string,
  blockedInbound: boolean,
  email: string
): Promise<AxiosResponse> {
  const token = await getToken();
  const resp = await axios({
    url: `/blacklist_file`,
    method: "post",
    data: {
      key: fileUrl,
      blocked_inbound: blockedInbound,
      posted_by: email
    },
    headers: {
      Authorization: token
    }
  });
  return resp;
}

// delete phone_number to blacklist
export async function deletePhoneNumberToBlacklist(
  phoneNumber: string
): Promise<AxiosResponse> {
  const token = await getToken();
  const resp = await axios({
    url: `/blacklist?phone_number=${phoneNumber}`,
    method: "delete",
    headers: {
      Authorization: token
    }
  });
  return resp;
}

export async function getDashboardStats(query: Object): Promise<AxiosResponse> {
  const queryParams = queryStringUtil.stringify(query);
  const token = await getToken();
  const resp = await axios({
    url: `${dashboardEndpoint}/dashboard${`?${queryParams}`}`,
    method: "get",
    headers: {
      Authorization: token
    }
  });
  return resp;
}

export async function getCommissions(query: Object): Promise<AxiosResponse> {
  const queryParams = queryStringUtil.stringify(query);
  const token = await getToken();
  const resp = await axios({
    url: `${dashboardEndpoint}/sale?${queryParams}`,
    method: "get",
    headers: {
      Authorization: token
    }
  });
  return resp;
}

export async function getMetrics(query: Object): Promise<AxiosResponse> {
  const queryParams = queryStringUtil.stringify(query);
  const token = await getToken();
  const resp = await axios({
    url: `${dashboardEndpoint}/metric?${queryParams}`,
    method: "get",
    headers: {
      Authorization: token
    }
  });
  return resp;
}
